<template>
  <component :is="linkComponent" v-bind="linkProps" class="insights-card" :class="`accent-${accentIndex}`">
    <div class="card-top">
      <div class="card-icon-wrap">
        <span class="card-icon" v-html="cardIcon"></span>
      </div>
      <div class="card-body">
        <h3 class="card-title">{{ item.title }}</h3>
        <p class="card-desc" v-if="item.subtitle">{{ item.subtitle }}</p>
      </div>
    </div>

    <div class="card-footer">
      <span class="card-link">{{ $t("learnMore") }} →</span>
    </div>
  </component>
</template>

<script>
export default {
  name: 'HighlightedFiaInsightsCard',
  props: {
    item: {
      type: Object,
      required: true
    },
    accentIndex: {
      type: Number,
      default: 0
    }
  },
  computed: {
    iconKey() {
      return (this.item.slug || this.item.title || '')
        .toLowerCase()
        .trim()
        .replace(/[čć]/g, 'c')
        .replace(/š/g, 's')
        .replace(/đ/g, 'd')
        .replace(/ž/g, 'z')
        .replace(/\s+/g, '-');
    },

    cardIcon() {
      const icons = {
        'fia-heritage-centar': `<i class="fa fa-university"></i>`,
        'heritage': `<i class="fa fa-university"></i>`,
        'fia-knowledge-hub': `<i class="fa fa-book"></i>`,
        'knowledge': `<i class="fa fa-book"></i>`,
        'fia-insights': `<i class="fa fa-line-chart"></i>`,
        'insights': `<i class="fa fa-line-chart"></i>`,
        'fia-gateway': `<i class="fa fa-credit-card"></i>`,
        'gateway': `<i class="fa fa-credit-card"></i>`,
      };

      if (icons[this.iconKey]) return icons[this.iconKey];
      for (const key of Object.keys(icons)) {
        if (this.iconKey.includes(key) || key.includes(this.iconKey)) {
          return icons[key];
        }
      }

      const fallbacks = [
        `<i class="fa fa-university"></i>`,
        `<i class="fa fa-book"></i>`,
        `<i class="fa fa-line-chart"></i>`
      ];
      return fallbacks[this.accentIndex % fallbacks.length];
    },

    isExternalLink() {
      return this.item.url && this.item.url.startsWith('http');
    },

    linkComponent() {
      return this.isExternalLink ? 'a' : 'router-link';
    },

    linkProps() {
      if (this.isExternalLink) {
        return { href: this.item.url, target: '_blank' };
      }
      if (this.item.url && !this.item.url.startsWith('http')) {
        return { to: { name: this.item.url } };
      }
      return {
        to: {
          name: 'fiaIntelligenceDetails',
          params: { id: this.item.id, slug: this.item.slug }
        }
      };
    }
  }
}
</script>

<style scoped>
.insights-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 24px 22px 20px;
  border-radius: 14px;
  background: #fafcff;
  border: 1px solid #e8eef6;
  text-decoration: none;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  min-height: 180px;
}

.insights-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.1);
  text-decoration: none;
}

.card-top {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}

.card-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

.accent-0 .card-icon-wrap {
  background: #dbeafe;
  color: #1d4ed8;
}

.accent-0:hover {
  border-color: #bfdbfe;
}

.accent-1 .card-icon-wrap {
  background: #ffedd5;
  color: #ea580c;
}

.accent-1:hover {
  border-color: #fed7aa;
}

.accent-2 .card-icon-wrap {
  background: #dcfce7;
  color: #16a34a;
}

.accent-2:hover {
  border-color: #bbf7d0;
}

.accent-3 .card-icon-wrap,
.accent-4 .card-icon-wrap {
  background: #ede9fe;
  color: #7c3aed;
}

.accent-3:hover,
.accent-4:hover {
  border-color: #ddd6fe;
}


/* ── Body text ── */
.card-body {
  flex: 1;
  padding-top: 0;
}

.card-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e3a5f;
  margin: 0 0 8px;
  line-height: 1.3;
}

.card-desc {
  font-size: 16px;
  color: #64748b;
  margin: 0;
  line-height: 1.55;
}

/* ── Footer link ── */
.card-footer {
  margin-top: auto;
  padding-top: 14px;
}

.card-link {
  font-size: 14px;
  font-weight: 600;
  /* color: #2563eb; */
  color: #ea580c;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: transform 0.2s ease, color 0.2s ease;
}

.insights-card:hover .card-link {
  transform: translateX(3px);
  color: #1d4ed8;
}
</style>